import { useI18n } from '@superbet-group/web.lib.vue-utilities';
import { ref, computed } from '@vue/composition-api';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
function useCurrencyDisplay(value, shouldFillDecimals, inputRef, emitUpdate) {
    var formatCurrency = useI18n().formatCurrency;
    var decimalSeparator = formatCurrency(11.11).toString().includes('.') ? '.' : ',';
    var wrongDecimalSeparator = decimalSeparator === '.' ? ',' : '.';
    var lastRecordedCursorPosition = ref(0);
    var forcedUpdateCounter = ref(0);
    var didStartWritingDecimal = ref(false);
    var previousDidStartWritingDecimal = ref(false);
    var lastEmittedValueSnapshot = ref();
    var displayValue = computed(function () { return valueAsString(value.value); });
    var alreadyContainsDecimalSeparator = computed(function () { return displayValue.value.includes(decimalSeparator); });
    function valueAsString(value) {
        // Hack: We need this to perform autoformatting if the input has changed,
        // but it has not affected the internal value
        forcedUpdateCounter.value;
        if (value == null) {
            return '';
        }
        var stringValue = value.toString().replace(wrongDecimalSeparator, decimalSeparator);
        if (shouldFillDecimals.value) {
            return trimToTwoDecimals(fillDecimals(stringValue));
        }
        else if (didStartWritingDecimal.value) {
            return "".concat(stringValue).concat(decimalSeparator);
        }
        else if (lastEmittedValueSnapshot.value && isBetterExpressedAsFirst(lastEmittedValueSnapshot.value, stringValue)) {
            return trimToTwoDecimals(lastEmittedValueSnapshot.value);
        }
        else {
            return trimToTwoDecimals(stringValue);
        }
    }
    function trimToTwoDecimals(value) {
        if (!value) {
            return value;
        }
        var _a = value.split(decimalSeparator); _a[0]; var decimal = _a[1];
        if (decimal && decimal.length > 2) {
            return value.slice(0, -(decimal.length - 2));
        }
        else {
            return value;
        }
    }
    function handleKeyPress(e) {
        var _a;
        var pressedChar = e.key;
        var charCode = (_a = e.which) !== null && _a !== void 0 ? _a : e.keyCode;
        var separators = [',', '.'];
        if (separators.includes(pressedChar) && alreadyContainsDecimalSeparator.value) {
            // If we have decimals enabled, we allow duplicate separators as that logic will take care of them
            // This allows to naturally write decimals and go over decimal separator by pressing the key
            if (!shouldFillDecimals.value) {
                updateDidStartWritingDecimal(false);
                e.preventDefault();
            }
        }
        else {
            updateDidStartWritingDecimal(separators.includes(pressedChar));
        }
        if (charCode > 31 && (charCode < 48 || charCode > 57) && !separators.includes(pressedChar)) {
            e.preventDefault();
        }
    }
    function handleInput(stringValue) {
        snapshotCursorPosition();
        var targetValue = !stringValue || Number.isNaN(parseFloat(stringValue))
            ? ''
            : stringValue;
        if (shouldFillDecimals.value || didStartWritingDecimal.value) {
            targetValue = fillDecimals(targetValue);
        }
        else if (!shouldFillDecimals.value && previousDidStartWritingDecimal.value && !alreadyContainsDecimalSeparator.value) {
            targetValue = targetValue.replace(/(\d)$/, "".concat(decimalSeparator, "$1"));
        }
        if (!targetValue) {
            emitUpdate(null);
            lastEmittedValueSnapshot.value = null;
        }
        else {
            emitUpdate(transformToNumber(targetValue));
            lastEmittedValueSnapshot.value = targetValue;
        }
        // Hack: Input happened, so force re-render even if the value
        // has remained the same
        forcedUpdateCounter.value += 1;
        if (shouldFillDecimals.value) {
            setCursorPosition();
        }
    }
    function handleBeforeInputEdgeCases(e) {
        var _a, _b, _c;
        if (!shouldFillDecimals.value) {
            return;
        }
        // Disallow leading 0 so when it gets formatted we don't have to manually compensate
        // for the missing 0'es in lastCursorPosition
        if (e.data === '0' && lastRecordedCursorPosition.value === 0 && ((_a = e.target) === null || _a === void 0 ? void 0 : _a.value)) {
            e.preventDefault();
            e.stopImmediatePropagation();
        }
        if (e.inputType === 'deleteContentForward' || e.inputType === 'deleteContentBackward') {
            var lookAheadOffset = e.inputType === 'deleteContentForward' ? 0 : -1;
            var currentValue = (_c = (_b = e.target) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : '';
            var modifiedCharacter = currentValue.charAt(lastRecordedCursorPosition.value + lookAheadOffset);
            // Prevent default and only move the cursor when user is trying to delete the comma separator in any way
            // This prevents decimal bleeding into the normal number when removing the decimal separator
            if (modifiedCharacter === decimalSeparator) {
                e.preventDefault();
                e.stopImmediatePropagation();
                setCursorPosition(e.inputType === 'deleteContentForward' ? 1 : -1);
            }
        }
    }
    function handleInputClick() {
        var _a;
        (_a = inputRef.value) === null || _a === void 0 ? void 0 : _a.setSelectionRange(0, displayValue.value.length);
        snapshotCursorPosition();
        updateDidStartWritingDecimal(false);
    }
    function handleInputBlur() {
        if (!shouldFillDecimals.value && displayValue.value.endsWith(decimalSeparator)) {
            var updatedValue = displayValue.value.slice(0, -1);
            updateDidStartWritingDecimal(false, true);
            emitUpdate(transformToNumber(updatedValue));
            lastEmittedValueSnapshot.value = updatedValue;
            // Hack: Input happened, so force re-render even if the value
            // has remained the same
            forcedUpdateCounter.value += 1;
        }
    }
    function snapshotCursorPosition() {
        var _a, _b;
        lastRecordedCursorPosition.value = (_b = (_a = inputRef.value) === null || _a === void 0 ? void 0 : _a.selectionEnd) !== null && _b !== void 0 ? _b : 0;
    }
    function setCursorPosition(offset) {
        if (offset === void 0) { offset = 0; }
        setTimeout(function () {
            var _a, _b;
            var cursorPosition = ((_a = lastRecordedCursorPosition.value) !== null && _a !== void 0 ? _a : 0) + offset;
            lastRecordedCursorPosition.value = cursorPosition;
            (_b = inputRef.value) === null || _b === void 0 ? void 0 : _b.setSelectionRange(cursorPosition, cursorPosition);
        }, 0);
    }
    function fillDecimals(value) {
        if (value === void 0) { value = ''; }
        var _a = value.split(decimalSeparator), whole = _a[0], decimalsRaw = _a[1];
        var decimals = decimalsRaw ? "".concat(decimalsRaw, "00").slice(0, 2) : '00';
        return "".concat(whole).concat(decimalSeparator).concat(decimals);
    }
    function transformToNumber(value) {
        if (!value) {
            return 0;
        }
        var _a = value
            .replace("".concat(decimalSeparator).concat(decimalSeparator), decimalSeparator)
            .split(decimalSeparator), whole = _a[0], decimals = _a[1];
        var wholePart = whole === null || whole === void 0 ? void 0 : whole.replace(/\D/g, '');
        var decimalPart = decimals ? decimals.replace(/\D/g, '') : '0';
        return parseFloat("".concat(wholePart, ".").concat(decimalPart));
    }
    function isBetterExpressedAsFirst(a, b) {
        if (a.endsWith("".concat(decimalSeparator, "0"))) {
            return true;
        }
        if (a.endsWith("".concat(decimalSeparator, "00"))) {
            return false;
        }
        return a !== b && trimToTwoDecimals(fillDecimals(a)) === trimToTwoDecimals(fillDecimals(b));
    }
    function updateDidStartWritingDecimal(value, overwriteHistory) {
        if (overwriteHistory === void 0) { overwriteHistory = false; }
        previousDidStartWritingDecimal.value = overwriteHistory ? value : didStartWritingDecimal.value;
        didStartWritingDecimal.value = value;
    }
    return {
        displayValue: displayValue,
        handleKeyPress: handleKeyPress,
        handleInput: handleInput,
        handleInputBlur: handleInputBlur,
        handleInputClick: handleInputClick,
        handleBeforeInputEdgeCases: handleBeforeInputEdgeCases,
    };
}

export { useCurrencyDisplay };
